import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useActivityLogs } from '../hooks/useActivityLogs';
import { LOG_ACTIONS, LOG_ACTIONS_MAP } from '../constant/logActions';

const formatTimestamp = (value) =>
  new Date(value).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });

function ActionBadge({ action }) {
  const meta = LOG_ACTIONS_MAP[action];
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${meta?.color ?? 'bg-gray-100 text-gray-600'}`}>
      {meta?.label ?? action}
    </span>
  );
}

function ActivityLogsPanel() {
  const { logs, loading, loadingMore, error, action, setAction, hasMore, loadMore, refetch } = useActivityLogs();

  return (
    <section className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col sm:h-full">
      <div className="shrink-0 px-5 py-4 bg-navy-light flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Activity Logs</h2>
          <p className="text-sm text-gray-200">Signups, logins, and record changes across the portal</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="rounded-md border-0 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">All Actions</option>
            {LOG_ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={refetch}
            aria-label="Refresh"
            className="p-2 rounded-md border border-white/30 text-white hover:bg-white/10"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="sm:flex-1 sm:min-h-0 sm:overflow-auto">
        {loading ? (
          <p className="px-5 py-8 text-sm text-gray-500 text-center">Loading activity…</p>
        ) : error ? (
          <p className="px-5 py-8 text-sm text-danger text-center">{error}</p>
        ) : logs.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500 text-center">No activity recorded yet</p>
        ) : (
          <>
            <table className="hidden sm:table w-full text-sm text-left border-collapse">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-gray-200 text-md text-gray-500 uppercase">
                  <th className="py-2 pl-5 pr-4 font-semibold whitespace-nowrap">When</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">User</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Action</th>
                  <th className="py-2 pr-5 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gold/10">
                    <td className="py-3 pl-5 pr-4 text-gray-500 whitespace-nowrap">{formatTimestamp(log.created_at)}</td>
                    <td className="py-3 pr-4 text-gray-900 font-medium whitespace-nowrap">
                      {log.actor_name || log.actor_email || 'Unknown'}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <ActionBadge action={log.action} />
                    </td>
                    <td className="py-3 pr-5 text-gray-700">{log.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="sm:hidden divide-y divide-gray-100">
              {logs.map((log) => (
                <div key={log.id} className="p-4 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <ActionBadge action={log.action} />
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(log.created_at)}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{log.actor_name || log.actor_email || 'Unknown'}</p>
                  <p className="text-sm text-gray-600">{log.description}</p>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="p-4 text-center border-t border-gray-100">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="text-sm font-medium text-navy-light hover:underline disabled:opacity-50"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default ActivityLogsPanel;
